import React, { useEffect, useState } from 'react';
import { SecureModule } from '../../security/SecureModule';
import Role, { ROLES, ROLES_DESCRIPTIONS, ROLES_PERMISSIONS_MAPPING, ROLES_POWER_ACCESS, hasRole } from '../../security/Role';
import Permission, { hasPermission, PERMISSIONS_DESCRIPTIONS, PERMISSIONS_USERS } from '../../security/Permission';
import { Autocomplete, Avatar, Box, Button, Checkbox, Container, Divider, Grid, IconButton, List, ListItem, ListItemButton, ListItemText, Paper, Stack, TextField, Tooltip, Typography } from '@mui/material';
import { AddRounded, BadgeRounded, RemoveRounded } from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import { Employee } from '../../types/Employee';
import { queryEmployee } from '../../utils/queryEmployee';
import { doc, getDoc, getFirestore, setDoc, updateDoc } from 'firebase/firestore';
import { useAppSelector } from '../../store';

const getPowerRole = (roles: string[]): Role | null => {
  if (roles.includes('super')) return 'super';
  if (roles.includes('admin')) return 'admin';
  return null;
};

const imMorePowerful = (me: Role | null, other: Role | null) => {
  if (me === 'super' && other !== 'super') return true;
  if (me === 'admin' && other !== 'admin' && other !== 'super') return true;
  return false;
}

type RolesAndPermissions = {
  roles: string[];
  permissions: string[];
}

type User = {
  loginId: string;
  rolesAndPermissions: RolesAndPermissions;
  profile: Employee;
}

type RolesPermissionsOption = {
  key: string;
  name: string; 
  description: string;
};

export const ManageUserPage = () => {
  const { loginId } = useParams();
  const auth = useAppSelector(s => s.auth);
  const db = getFirestore();

  const [user, setUser] = useState<User | null>(null);

  const [selectedRole, setSelectedRole] = useState<RolesPermissionsOption | null>(null);
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  
  const [selectedPermission, setSelectedPermission] = useState<RolesPermissionsOption | null>(null);
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>([]);

  const [powerAccess, setPowerAccess] = useState(false);

  const getDataRolesAndPermissions = async () => {
    if (loginId) {
      const dDoc = await getDoc(doc(db, `Admins/${loginId}`));
      if (dDoc.exists()) {
        return dDoc.data() as RolesAndPermissions;
      }

      return null;
    }
  };

  const updateRoles = async (newRoles: string[]) => {
    if (user) {
      await setDoc(doc(db, `Admins/${loginId}`), { roles: newRoles }, { merge: true });
      setUser({
        ...user, 
        rolesAndPermissions: {
          roles: newRoles,
          permissions: user.rolesAndPermissions.permissions,
        },
      });
    }
  };

  const updatePermissions = async (newPermissions: string[]) => {
    if (user) {
      await setDoc(doc(db, `Admins/${loginId}`), { permissions: newPermissions }, { merge: true });
      setUser({
        ...user, 
        rolesAndPermissions: {
          roles: user.rolesAndPermissions.roles,
          permissions: newPermissions,
        },
      });
    }
  };

  const handleToggleRole = (role: string) => () => {
    const currentIndex = selectedRoles.indexOf(role);
    const newSelectedRoles = [...selectedRoles];
    if (currentIndex === -1) {
      newSelectedRoles.push(role);
    } else {
      newSelectedRoles.splice(currentIndex, 1);
    }
    setSelectedRoles(newSelectedRoles);
  };

  const handleAddRoles = () => {
    if (hasPermission(auth, undefined, ['update.users'])) {
      if (user && selectedRole) {
        const newRoles = [...user.rolesAndPermissions.roles, selectedRole.key];
        updateRoles(newRoles).then(() => {
          setSelectedRole(null);
        });
      }
    } else {
      window.alert('You are not authorized to perform this action.');
    }
  };

  const handleRemoveRoles = () => {
    if (hasPermission(auth, undefined, ['update.users'])) {
      if (user) {
        const newRoles = user.rolesAndPermissions.roles.filter(r => !selectedRoles.includes(r));
        updateRoles(newRoles).then(() => {
          // Clear selected roles
          setSelectedRoles([]);
        });
      }
    } else {
      window.alert('You are not authorized to perform this action.');
    }
  };

  const handleTogglePermission = (permission: string) => () => {
    const currentIndex = selectedPermissions.indexOf(permission);
    const newSelectedPermissions = [...selectedPermissions];
    if (currentIndex === -1) {
      newSelectedPermissions.push(permission);
    } else {
      newSelectedPermissions.splice(currentIndex, 1);
    }
    setSelectedPermissions(newSelectedPermissions);
  };

  const handleAddPermissions = () => {
    if (hasPermission(auth, undefined, ['update.users'])) {
      if (user && selectedPermission) {
        const newPermissions = [...user.rolesAndPermissions.permissions, selectedPermission.key];
        updatePermissions(newPermissions).then(() => {
          setSelectedPermission(null);
        });
      }
    }
  };

  const handleRemovePermissions = () => {
    if (hasPermission(auth, undefined, ['update.users'])) {
      if (user) {
        const newPermissions = user.rolesAndPermissions.permissions.filter(p => !selectedPermissions.includes(p));
        updatePermissions(newPermissions).then(() => {
          // Clear selected permissions
          setSelectedPermissions([]);
        });
      }
    } else {
      window.alert('You are not authorized to perform this action.');
    }
  };

  useEffect(() => {
    getDataRolesAndPermissions().then((rolesAndPermissions) => {
        if (rolesAndPermissions) {
          return queryEmployee(loginId!).then(profile => {
            if (profile) {
              return {
                loginId,
                rolesAndPermissions,
                profile,
              } as User;
            }

            return null;
          }).catch(() => {
            return null;
          });
        }
        return null;
      }).then(info => {
        if (info) {
          setUser(info);
        }
      });
  }, [loginId]);

  useEffect(() => {
    if (user) {
      const me = getPowerRole(auth.URB.roles);
      const other = getPowerRole(user.rolesAndPermissions.roles);
      setPowerAccess(imMorePowerful(me, other));
    }
  }, [user]);

  return (
    <SecureModule permissions={PERMISSIONS_USERS}>
      <Container disableGutters>
        {user !== null && (
          <>
            <Paper 
              sx={{ 
                mt: 2, 
                mb: 4, 
                p: 3,
                position: 'relative',
                overflow: 'hidden',
              }}
              elevation={4}
            >
              <div style={{
                backgroundImage: `url(${user.profile.picture})`, 
                backgroundSize: 'cover',
                backgroundPosition: 'center center', 
                filter: 'blur(5px)',
                position: 'absolute',
                left: 0,
                bottom: 0,
                right: 0,
                top: 0,
                zIndex: 0,
              }} />
              <div style={{ display: 'flex', flexDirection: 'row', position: 'relative', zIndex: 100 }}>
                <Avatar alt={user.profile.name} src={user.profile.picture} sx={{ width: 56, height: 56 }}/>
                <Box ml={4} flexGrow={1}>
                  <Typography variant="h4">{user.profile.name}</Typography>
                  <Box display="flex" flexDirection="row">
                    <BadgeRounded color="info" sx={{ mr: 1 }} />
                    <Typography variant="subtitle1" fontWeight={700} color="#808080">{user.profile.idNumber}</Typography>
                  </Box>
                </Box>
              </div>
            </Paper>
            <Grid container spacing={1}>
              <Grid item xs={6}>
                <Stack>
                  <Typography variant='h4'>ROLES</Typography>
                  <Box mt={1} mb={1}>
                    <List sx={{ 
                      width: '100%', 
                      borderRadius: 1,
                      bgcolor: (theme) => {
                        return theme.palette.mode === 'dark' ? '#ffffff0f' : '#f2f2f2';
                      },
                    }}>
                      {user.rolesAndPermissions.roles.map(r => {
                        let displayName = r;
                        let description = '';
                        if (ROLES_DESCRIPTIONS.hasOwnProperty(r)) {
                          displayName = ROLES_DESCRIPTIONS[r as Role].name;
                          description = ROLES_DESCRIPTIONS[r as Role].description;
                        }

                        return (
                          <ListItem 
                            key={r} 
                            disablePadding
                            secondaryAction={
                              powerAccess ? (
                                <Checkbox
                                  edge="end"
                                  onChange={handleToggleRole(r)}
                                  checked={selectedRoles.indexOf(r) !== -1}
                                  inputProps={{ 'aria-labelledby': r }}
                                />
                              ) : (
                                null
                              )
                            }
                          >
                            {powerAccess ? (
                              <ListItemButton>
                                <ListItemText primary={`${displayName}`} />
                              </ListItemButton>
                            ) : (
                              <ListItemText sx={{ margin: 0, paddingX: 2, paddingY: 1 }} primary={`${displayName}`} />
                            )}
                          </ListItem>
                        );
                      })}
                    </List>
                  </Box>
                  {powerAccess ? (
                    <Stack direction='row' spacing={1}>
                      <Autocomplete 
                        options={Object.entries(ROLES_DESCRIPTIONS).filter(([key, value]) => !user.rolesAndPermissions.roles.includes(key)).map<RolesPermissionsOption>(([key, value]) => {
                          return {
                            key: key,
                            name: value.name,
                            description: value.description,
                          };
                        })}
                        value={selectedRole}
                        onChange={(e: any, value: RolesPermissionsOption | null) => {
                          setSelectedRole(value);
                        }}
                        getOptionLabel={(opt) => opt.name}
                        isOptionEqualToValue={(opt) => opt.key === selectedRole?.key}
                        renderInput={(params) => <TextField {...params} size='small' hiddenLabel />}
                        sx={{
                          flexGrow: 1,
                        }}
                      />
                      <Button variant='outlined' disabled={selectedRole === null} onClick={handleAddRoles}>
                        <AddRounded />
                      </Button>
                      <Button variant='contained' color='error' disabled={selectedRoles.length === 0} onClick={handleRemoveRoles}>
                        <RemoveRounded />
                      </Button>
                    </Stack>
                  ) : (
                    <Box mx={2}>
                      <Typography variant='caption' fontStyle='italic' color='text.secondary'>
                        You are not allowed to update this user.
                      </Typography>
                    </Box>
                  )}
                </Stack>
              </Grid>
              <Grid item xs={6}>
                <Stack>
                  <Typography variant='h4'>PERMISSIONS</Typography>
                  <Box mt={1} mb={1}>
                    {/** Get list of permissions from assigned roles */}
                    <List sx={{ 
                      width: '100%', 
                      borderRadius: 1,
                      bgcolor: (theme) => {
                        return theme.palette.mode === 'dark' ? '#ffffff0f' : '#f2f2f2';
                      },
                    }}>
                      {user.rolesAndPermissions.roles.map(r => {
                        let displayName = r;
                        let description = '';
                        if (ROLES_DESCRIPTIONS.hasOwnProperty(r)) {
                          displayName = ROLES_DESCRIPTIONS[r as Role].name;
                          description = ROLES_DESCRIPTIONS[r as Role].description;
                        }

                        return (
                          <ListItem key={r} disablePadding>
                            <ListItemText sx={{ margin: 0, paddingX: 2, paddingY: 1 }} primary={
                              <Typography variant='caption'>
                                All <Typography variant='caption' component='span' color='primary.dark'><strong>{displayName}</strong></Typography> role permissions.
                              </Typography>
                            } />
                          </ListItem>
                        );
                      })}

                      {user.rolesAndPermissions.roles.length > 0 && user.rolesAndPermissions.permissions.length > 0 && (
                        <Divider />
                      )}

                      {user.rolesAndPermissions.permissions.map(p => {
                        let displayName = p;
                        let description = '';
                        if (PERMISSIONS_DESCRIPTIONS.hasOwnProperty(p)) {
                          displayName = PERMISSIONS_DESCRIPTIONS[p as Permission].name;
                          description = PERMISSIONS_DESCRIPTIONS[p as Permission].description;
                        }

                        return (
                          <ListItem 
                            key={p} 
                            disablePadding
                            secondaryAction={
                              powerAccess ? (
                                <Checkbox
                                  edge="end"
                                  onChange={handleTogglePermission(p)}
                                  checked={selectedPermissions.indexOf(p) !== -1}
                                  inputProps={{ 'aria-labelledby': p }}
                                />
                              ) : (
                                null
                              )
                            }
                          >
                            <ListItemButton>
                              <ListItemText primary={`${displayName}`} />
                            </ListItemButton>
                          </ListItem>
                        );
                      })}
                    </List>
                  </Box>
                  {powerAccess ? (
                    <Stack direction='row' spacing={1}>
                      <Autocomplete 
                        options={Object.entries(PERMISSIONS_DESCRIPTIONS).filter(([key, value]) => !user.rolesAndPermissions.roles.some(r => ROLES_PERMISSIONS_MAPPING[r as Role].includes(key as Permission)) && !user.rolesAndPermissions.permissions.includes(key)).map<RolesPermissionsOption>(([key, value]) => {
                          return {
                            key: key,
                            name: value.name,
                            description: value.description,
                          };
                        })}
                        value={selectedPermission}
                        onChange={(e: any, value: RolesPermissionsOption | null) => {
                          setSelectedPermission(value);
                        }}
                        getOptionLabel={(opt) => opt.name}
                        isOptionEqualToValue={(opt) => opt.key === selectedPermission?.key}
                        renderInput={(params) => <TextField {...params} size='small' hiddenLabel />}
                        sx={{
                          flexGrow: 1,
                        }}
                      />
                      <Button variant='outlined' disabled={selectedPermission === null} onClick={handleAddPermissions}>
                        <AddRounded />
                      </Button>
                      <Button variant='contained' color='error' disabled={selectedPermissions.length === 0} onClick={handleRemovePermissions}>
                        <RemoveRounded />
                      </Button>
                    </Stack>
                  ) : (
                    <Box mx={2}>
                      <Typography variant='caption' fontStyle='italic' color='text.secondary'>
                        You are not allowed to update this user.
                      </Typography>
                    </Box>
                  )}
                </Stack>
              </Grid>
            </Grid>
          </>
        )}
      </Container>
    </SecureModule>
  );
};
